import head from "lodash/head";
import isEmpty from "lodash/isEmpty";
import last from "lodash/last";
import { sortOverbookings } from "PFApp/booking/components/details_panel/detail_view/overbookings_detail_side_panel/use_overbooking_ranges";
import { Overbooking } from "PFApp/booking/types";
import { Button } from "PFComponents/button";
import { Stack } from "PFComponents/containers";
import { Activity, BookingCategory, CalendarRange, Id } from "PFTypes";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { DetailsPanelSource, OverbookingPotentialBooking } from "../../details_panel";
import WarningIcons from "../../warning_icons/warning_icons";
import { BookingFormProps } from "../booking_form";
import { BookingFormMode } from "../booking_form.types";
import { isEditMode } from "../booking_form.utils";
import { BookingFormValues } from "../booking_form_provider";
import { PotentialWarnings } from "../use_potential_warnings";
import { BookingFormErrorMessage } from "./booking_form_error_message";
import { CreateButton } from "./create_button";
import { CreateWithOverbookingButtonGroup } from "./create_with_overbooking_button_group";
import css from "./footer.module.scss";

const getCalendarRange = (overbookings?: Overbooking[]): CalendarRange | undefined =>
  !isEmpty(overbookings) ? { min: head(overbookings)!.date, max: last(overbookings)!.date } : undefined;

type FooterProps = {
  profileId: Id | undefined;
  activity: Activity | undefined;
  onClose: () => void;
  isSubmitDisabled: boolean;
  isLoading: boolean;
  mode: BookingFormMode;
  overbookingIntersectionsDates: string[] | undefined;
  source?: DetailsPanelSource;
} & Pick<PotentialWarnings, "overbookings" | "misalignments"> &
  Pick<BookingFormProps, "onOpenOverbookingsManagementModal">;

export const Footer = ({
  profileId,
  activity,
  overbookings,
  misalignments,
  overbookingIntersectionsDates,
  onClose,
  isSubmitDisabled,
  isLoading,
  mode,
  onOpenOverbookingsManagementModal,
  source
}: FooterProps) => {
  const { t } = useTranslation();
  const [bookings, globalCategory, globalTitle] = useWatch<
    BookingFormValues,
    ["bookings", "globalCategory", "globalTitle"]
  >({
    name: ["bookings", "globalCategory", "globalTitle"]
  });

  const potentialBookings = !isEditMode(mode)
    ? ((bookings ?? []).map(({ key, title, category, startDate, endDate }) => ({
        key,
        title: title ?? globalTitle,
        booking_category_id: category?.id ?? globalCategory?.id,
        start_date: startDate,
        end_date: endDate
      })) as OverbookingPotentialBooking[])
    : [];

  const getConfirmButtonLabel = () => {
    if (isEditMode(mode)) {
      return t("update");
    } else {
      return t("create");
    }
  };

  const hasOverbookingsCausedByOtherBookings = (overbookingIntersectionsDates ?? []).length > 0;
  const isCreateMode = mode === BookingFormMode.Create;

  const shouldDisplayOverbookingButtonGroup =
    !isEditMode(mode) &&
    hasOverbookingsCausedByOtherBookings &&
    (isCreateMode
      ? (globalCategory as BookingCategory)?.affects_availability
      : (bookings ?? [])
          .map((booking) =>
            booking?.category ? (booking?.category as BookingCategory)?.affects_availability : false
          )
          .reduce((acc, curr) => acc || curr, false));

  const sortedOverbookings = sortOverbookings(overbookings);
  const overbookingsCalendarRange = getCalendarRange(sortedOverbookings);

  return (
    <div className={css.root}>
      <Stack gap="spacingLg">
        <BookingFormErrorMessage />
        <div className={css.buttonsSection}>
          <Button
            icon={shouldDisplayOverbookingButtonGroup ? "cross" : undefined}
            type="button"
            kind="secondary"
            text={!shouldDisplayOverbookingButtonGroup ? t("cancel") : undefined}
            onClick={onClose}
          />
          <div className={css.content}>
            <WarningIcons
              profileId={profileId}
              activityIds={activity ? [activity.id] : []}
              overbookings={overbookings}
              overbookingsCalendarRange={overbookingsCalendarRange}
              misalignments={misalignments}
              potentialBookings={potentialBookings}
              source={source}
              arePotentialWarnings
            />
          </div>
          {shouldDisplayOverbookingButtonGroup ? (
            <CreateWithOverbookingButtonGroup
              isSubmitDisabled={isSubmitDisabled}
              isLoading={isLoading}
              onOpenOverbookingsManagementModal={onOpenOverbookingsManagementModal}
              overbookingIntersectionsDates={overbookingIntersectionsDates ?? []}
            />
          ) : (
            <CreateButton label={getConfirmButtonLabel()} isLoading={isLoading} disabled={isSubmitDisabled} />
          )}
        </div>
      </Stack>
    </div>
  );
};
